/* Collection-only browsing memory. Product markup and prices always come from Shopify. */
(() => {
  if (window.Shopify?.designMode || window.blinkCollectionMemoryLoaded) return;
  window.blinkCollectionMemoryLoaded = true;

  const STATE_KEY = 'blinkCollectionMemory';
  const STORAGE_PREFIX = 'blink:collection-memory:v1:';
  const MAX_AGE = 30 * 60 * 1000;
  const MAX_ENTRIES = 20;
  const MAX_BATCHES = 60;
  const instances = new Map();

  function read(id) {
    try {
      const record = JSON.parse(sessionStorage.getItem(STORAGE_PREFIX + id));
      if (!record || Date.now() - record.updatedAt > MAX_AGE) return null;
      return record;
    } catch (_) {
      return null;
    }
  }

  function write(id, record) {
    try {
      sessionStorage.setItem(STORAGE_PREFIX + id, JSON.stringify(record));
      const records = [];
      for (let index = 0; index < sessionStorage.length; index += 1) {
        const key = sessionStorage.key(index);
        if (key?.startsWith(STORAGE_PREFIX)) {
          const value = read(key.slice(STORAGE_PREFIX.length));
          records.push({ key, updatedAt: value?.updatedAt || 0 });
        }
      }
      records.sort((a, b) => b.updatedAt - a.updatedAt);
      records.forEach((item, index) => {
        if (!item.updatedAt || index >= MAX_ENTRIES) sessionStorage.removeItem(item.key);
      });
    } catch (_) {
      // Private browsing/storage limits must never prevent normal collection browsing.
    }
  }

  class CollectionMemory {
    constructor(root) {
      this.root = root;
      this.events = new AbortController();
      this.waiting = true;
      this.adoptEntry();
      const options = { signal: this.events.signal };
      this.pageHasShown = document.readyState === 'complete';
      window.addEventListener('pageshow', (event) => {
        this.pageHasShown = true;
        this.pageShown(event.persisted);
        requestAnimationFrame(() => {
          if (!this.restoring) this.releaseScrollRestoration();
        });
      }, options);
      window.addEventListener('pagehide', () => {
        this.save();
        this.stopImageRecovery();
        this.cancelRestore();
        this.releaseScrollRestoration(true);
        clearTimeout(this.saveTimer);
        this.waiting = true;
      }, options);
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') this.save();
      }, options);
      window.addEventListener('scroll', () => {
        if (this.restoring || this.waiting || this.suspended || this.correcting) return;
        this.clickedAnchor = null;
        clearTimeout(this.saveTimer);
        this.saveTimer = setTimeout(() => this.save(false), 250);
      }, { ...options, passive: true });
      // User intent wins over automatic reconstruction and late layout corrections.
      ['wheel', 'touchstart', 'pointerdown'].forEach((type) => {
        window.addEventListener(type, () => this.cancelRestore(), { ...options, passive: true });
      });
      window.addEventListener('keydown', (event) => {
        if (['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' '].includes(event.key)) this.cancelRestore();
      }, options);
      root.addEventListener('click', (event) => this.productClicked(event), { ...options, capture: true });
      root.addEventListener('collection:page-loaded', () => {
        if (!this.restoring) this.save();
      }, options);
      root.addEventListener('collection:results-changing', (event) => {
        // The active entry is retained separately: popstate already points at the destination.
        this.save();
        this.cancelRestore();
        this.suspended = true;
        this.clickedAnchor = null;
        this.fromHistory = event.detail.fromHistory;
      }, options);
      root.addEventListener('collection:results-changed', () => {
        this.adoptEntry();
        this.suspended = false;
        if (this.fromHistory) this.restore();
        else this.save();
      }, options);
      root.addEventListener('collection:results-error', () => {
        // On a failed history request, the visible grid still belongs to the old entry.
        // Keep its memory intact until a retry succeeds rather than saving it as the destination.
        this.suspended = this.fromHistory;
      }, options);
      this.pageShown(false);
    }

    context() {
      const url = new URL(window.location.href);
      url.searchParams.delete('section_id');
      url.searchParams.delete('preview_theme_id');
      url.searchParams.sort();
      return `${this.root.dataset.collectionMemory}:${this.root.dataset.memoryPageSize}:${this.root.dataset.memoryStyle}:${url.pathname}?${url.searchParams}`;
    }

    adoptEntry() {
      const key = this.context();
      const state = history.state && typeof history.state === 'object' ? history.state : {};
      const existing = state[STATE_KEY];
      this.entry = existing?.key === key && typeof existing.id === 'string'
        ? existing : { id: `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`, key };
      if (this.entry !== existing || !Object.hasOwn(state, 'searchParams')) {
        const params = new URL(window.location.href).searchParams;
        params.delete('preview_theme_id');
        params.delete('section_id');
        try {
          history.replaceState({ ...state, searchParams: params.toString(), [STATE_KEY]: this.entry }, '');
        } catch (_) {
          this.entry = null;
        }
      }
    }

    anchor(card) {
      return card ? { productId: card.dataset.productId, top: card.getBoundingClientRect().top } : null;
    }

    visibleAnchor() {
      for (const card of this.root.querySelectorAll('product-card[data-product-id]')) {
        const rect = card.getBoundingClientRect();
        if (rect.bottom > 0 && rect.top < window.innerHeight) return this.anchor(card);
      }
      return null;
    }

    productClicked(event) {
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const link = event.target.closest('a[href]');
      const card = link?.closest('product-card[data-product-id]');
      if (!card || link.hasAttribute('download') || (link.target && link.target !== '_self')) return;
      const url = new URL(link.href, window.location.href);
      if (url.origin !== window.location.origin || !url.pathname.includes('/products/')) return;
      this.cancelRestore();
      this.clickedAnchor = { ...this.anchor(card), keyboard: event.detail === 0 };
      this.save();
    }

    save(captureAnchor = true) {
      if (!this.entry || this.waiting || this.suspended || this.restoring || !this.root.isConnected) return;
      const bounds = this.root.getBoundingClientRect();
      write(this.entry.id, {
        key: this.entry.key,
        updatedAt: Date.now(),
        startPage: Number(this.root.dataset.memoryStartPage),
        endPage: Number(this.root.dataset.memoryEndPage),
        scrollY: window.scrollY,
        gridHeight: bounds.height,
        gridWidth: bounds.width,
        layout: this.root.dataset.layout,
        // Scroll checkpoints only store the offset; scan cards when leaving or changing results.
        anchor: this.clickedAnchor || (captureAnchor ? this.visibleAnchor() : null)
      });
    }

    pageShown(persisted) {
      if (!persisted && this.initialShown) return;
      this.initialShown = true;
      this.waiting = false;
      this.suspended = false;
      if (persisted) {
        this.startImageRecovery();
        this.restore(true).finally(() => this.settleImageRecovery());
        return;
      }
      const type = performance.getEntriesByType('navigation')[0]?.type;
      if (type === 'back_forward') {
        this.startImageRecovery();
        this.restore().finally(() => this.settleImageRecovery());
      } else if (type === 'reload') this.restore();
      else this.save();
    }

    imageRetryRequest(image) {
      const srcset = image.getAttribute('srcset');
      if (srcset) {
        const candidates = srcset.split(',').map(candidate => {
          const match = candidate.trim().match(/^(\S+)(\s+\d+w)$/);
          if (!match) return null;
          try {
            const url = new URL(match[1], location.href);
            const entries = [...url.searchParams.entries()];
            if (url.origin !== location.origin || !/^\/cdn\/shop\/(files|products)\//.test(url.pathname)
              || entries.length !== 2 || !url.searchParams.has('v') || !url.searchParams.has('width')) return null;
            url.search = '';
            entries.reverse().forEach(([key, value]) => url.searchParams.append(key, value));
            return url.href + match[2];
          } catch (_) { return null; }
        });
        if (candidates.length && candidates.every(Boolean)) {
          return { attribute: 'srcset', value: candidates.join(', '), strategy: 'responsive-url-refresh' };
        }
      }
      return { attribute: 'src', value: image.getAttribute('src'), strategy: 'source-refresh' };
    }

    startImageRecovery() {
      this.stopImageRecovery();
      const events = new AbortController();
      const attempted = new WeakSet();
      const observed = new WeakSet();
      this.imageRecoveryEvents = events;
      const retry = (image, failedEvent = false) => {
        if (!image?.matches?.('.bb-product-card img.card__main-image')
          || !this.root.contains(image) || !image.isConnected || image.hidden
          || image.closest('[hidden], product-card-image-slider') || attempted.has(image)
          || image.naturalWidth > 0 || (!failedEvent && (!image.complete || !image.currentSrc))) return;
        const src = image.getAttribute('src');
        if (!src || !/^https?:/i.test(new URL(src, location.href).protocol)) return;
        const box = image.getBoundingClientRect();
        if (!box.width || !box.height || box.bottom <= 0 || box.top >= innerHeight
          || box.right <= 0 || box.left >= innerWidth) return;
        attempted.add(image);
        this.imageRecoveryObserver?.unobserve(image);
        const request = this.imageRetryRequest(image);
        image.setAttribute(request.attribute, request.value);
      };
      // Keep watching pending/failed images for this history return. Native lazy
      // requests may fail after restoration, or only enter view much later.
      if ('IntersectionObserver' in window) {
        this.imageRecoveryObserver = new IntersectionObserver(entries => {
          if (events.signal.aborted) return;
          entries.forEach(entry => {
            if (entry.isIntersecting) retry(entry.target);
          });
        });
      }
      this.scanReturnedImages = () => {
        this.imageRecoveryFrame = null;
        if (events.signal.aborted) return;
        this.root.querySelectorAll('.bb-product-card img.card__main-image').forEach(image => {
          if (!observed.has(image) && !attempted.has(image) && !image.naturalWidth
            && !image.hidden && !image.closest('[hidden], product-card-image-slider')) {
            observed.add(image);
            this.imageRecoveryObserver?.observe(image);
          }
          retry(image);
        });
      };
      const schedule = () => {
        if (this.imageRecoveryFrame == null) this.imageRecoveryFrame = requestAnimationFrame(this.scanReturnedImages);
      };
      this.root.addEventListener('error', event => {
        const image = event.target;
        if (image?.matches?.('.bb-product-card img.card__main-image') && !attempted.has(image)
          && !image.hidden && !image.closest('[hidden], product-card-image-slider')) {
          this.imageRecoveryObserver?.observe(image);
        }
        retry(image, true);
      }, { capture: true, signal: events.signal });
      this.root.addEventListener('load', event => {
        if (event.target?.naturalWidth > 0) this.imageRecoveryObserver?.unobserve(event.target);
      }, { capture: true, signal: events.signal });
      this.root.addEventListener('collection:page-loaded', schedule, { signal: events.signal });
      // Scroll capture also covers horizontal carousels in the fallback path.
      if (!this.imageRecoveryObserver) {
        this.root.addEventListener('scroll', schedule, { capture: true, passive: true, signal: events.signal });
        window.addEventListener('scroll', schedule, { passive: true, signal: events.signal });
      }
      schedule();
    }

    settleImageRecovery() {
      if (!this.imageRecoveryEvents) return;
      cancelAnimationFrame(this.imageRecoveryFrame);
      this.scanReturnedImages();
    }

    stopImageRecovery() {
      this.imageRecoveryEvents?.abort();
      this.imageRecoveryEvents = null;
      this.imageRecoveryObserver?.disconnect();
      this.imageRecoveryObserver = null;
      cancelAnimationFrame(this.imageRecoveryFrame);
      this.imageRecoveryFrame = null;
    }

    releaseScrollRestoration(force = false) {
      if (this.previousRestoration == null || (!force && !this.pageHasShown)) return;
      if ('scrollRestoration' in history) history.scrollRestoration = this.previousRestoration;
      this.previousRestoration = null;
    }

    releaseSpace() {
      if (this.previousMinHeight != null) {
        this.root.style.minHeight = this.previousMinHeight;
        this.previousMinHeight = null;
      }
      const status = this.root.querySelector('.js-memory-status');
      if (status) status.hidden = true;
      this.root.removeAttribute('data-memory-loading');
    }

    cancelRestore() {
      clearTimeout(this.correctionTimer);
      this.resizeObserver?.disconnect();
      this.correcting = false;
      this.releaseSpace();
      if (!this.restoring) return;
      this.restoring = false;
      this.run = null;
      this.root.cancel?.();
      this.root.removeAttribute('data-memory-restoring');
      this.releaseScrollRestoration();
    }

    async restore(native = false) {
      const record = this.entry && read(this.entry.id);
      const startPage = Number(this.root.dataset.memoryStartPage);
      if (!record || record.key !== this.entry.key || record.startPage !== startPage
        || !Number.isInteger(record.endPage) || record.endPage < startPage
        || record.endPage - startPage >= MAX_BATCHES || !Number.isFinite(record.scrollY)) {
        this.save();
        return;
      }
      this.cancelRestore();
      const run = {};
      this.run = run;
      this.restoring = true;
      this.root.setAttribute('data-memory-restoring', 'true');
      if (this.previousRestoration == null) this.previousRestoration = history.scrollRestoration;
      if (!native && 'scrollRestoration' in history) history.scrollRestoration = 'manual';
      const missingPages = Number(this.root.dataset.memoryEndPage) < record.endPage;
      if (missingPages) {
        this.root.setAttribute('data-memory-loading', 'true');
        const bounds = this.root.getBoundingClientRect();
        if (Number.isFinite(record.gridHeight) && record.gridHeight > bounds.height
          && Math.abs(record.gridWidth - bounds.width) < 2 && record.layout === this.root.dataset.layout) {
          this.previousMinHeight = this.root.style.minHeight;
          this.root.style.minHeight = `${Math.ceil(record.gridHeight)}px`;
          window.scrollTo({ top: record.scrollY, behavior: 'instant' });
        }
        const status = this.root.querySelector('.js-memory-status');
        if (status) status.hidden = false;
      }
      try {
        while (this.run === run && Number(this.root.dataset.memoryEndPage) < record.endPage) {
          const before = Number(this.root.dataset.memoryEndPage);
          if (!this.root.hasMore?.() || !await this.root.loadMore(false)) break;
          if (Number(this.root.dataset.memoryEndPage) <= before) break;
        }
        if (this.run !== run || !this.root.isConnected) return;
        await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
        if (this.run !== run) return;
        this.releaseSpace();
        const id = record.anchor?.productId;
        const card = typeof id === 'string' && /^\d+$/.test(id)
          ? this.root.querySelector(`product-card[data-product-id="${id}"]`) : null;
        if (record.anchor?.keyboard) card?.querySelector('a.js-prod-link')?.focus({ preventScroll: true });
        const position = () => {
          const top = card && Number.isFinite(record.anchor.top)
            ? window.scrollY + card.getBoundingClientRect().top - Math.min(record.anchor.top, window.innerHeight - 80)
            : record.scrollY;
          const destination = Math.max(0, Math.min(top, document.documentElement.scrollHeight - window.innerHeight));
          if (Math.abs(window.scrollY - destination) > 1) window.scrollTo({ top: destination, behavior: 'instant' });
        };
        this.correcting = true;
        position();
        // Reserved image dimensions normally suffice; briefly correct for delayed section layout.
        if ('ResizeObserver' in window) {
          this.resizeObserver = new ResizeObserver(position);
          this.resizeObserver.observe(this.root);
        }
        this.correctionTimer = setTimeout(() => {
          this.resizeObserver?.disconnect();
          this.correcting = false;
          this.save();
        }, 1500);
      } catch (error) {
        console.warn('Unable to restore collection browsing position', error);
      } finally {
        if (this.run === run) {
          this.restoring = false;
          this.run = null;
          this.releaseSpace();
          this.root.removeAttribute('data-memory-restoring');
          this.releaseScrollRestoration();
          this.save();
        }
      }
    }

    destroy() {
      this.stopImageRecovery();
      this.save();
      this.cancelRestore();
      this.releaseScrollRestoration(true);
      clearTimeout(this.saveTimer);
      this.events.abort();
    }
  }

  function boot() {
    document.querySelectorAll('[data-collection-memory]').forEach((root) => {
      if (!instances.has(root)) instances.set(root, new CollectionMemory(root));
    });
    instances.forEach((instance, root) => {
      if (!root.isConnected) {
        instance.destroy();
        instances.delete(root);
      }
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();








